import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { cn } from '@/lib/utils';

/**
 * Renders Markdown guide bodies in HouseOps's "field manual"
 * typography. Newsreader for headings, IBM Plex Sans for body, IBM
 * Plex Mono for code. Mobile-forward sizes: full width below `md:`,
 * 3xl bound on desktop. Line-height tuned for outdoor reading.
 *
 * Per-element renderers apply Tailwind utilities directly rather than
 * relying on @tailwindcss/typography — keeps the dependency surface
 * small and the visual identity precisely on-brand.
 *
 * GFM gives tables, task lists, autolinks, strikethrough. External
 * links open in a new tab; relative links stay in-app.
 */
const components: Components = {
  h1: (p) => (
    <h1
      className="font-display text-3xl md:text-4xl font-semibold tracking-tight text-foreground mt-10 mb-5 first:mt-0"
      {...p}
    />
  ),
  h2: (p) => (
    <h2
      className="font-display text-2xl md:text-3xl font-semibold tracking-tight mt-10 mb-4 pt-5 border-t border-rule"
      {...p}
    />
  ),
  h3: (p) => <h3 className="font-display text-xl font-semibold tracking-tight mt-8 mb-3" {...p} />,
  h4: (p) => <h4 className="font-display text-lg font-medium mt-6 mb-2" {...p} />,
  p: (p) => <p className="my-4 leading-[1.75]" {...p} />,
  ul: (p) => <ul className="list-disc pl-6 my-4 space-y-1.5 marker:text-primary/70" {...p} />,
  ol: (p) => <ol className="list-decimal pl-6 my-4 space-y-1.5 marker:text-primary/70 marker:font-mono" {...p} />,
  li: (p) => <li className="leading-relaxed" {...p} />,
  blockquote: (p) => (
    <blockquote
      className="my-5 border-l-4 border-warning bg-warning/5 pl-4 py-2 italic text-foreground/80"
      {...p}
    />
  ),
  code: ({ className, children, ...p }) => {
    const isBlock = String(className ?? '').includes('language-') || String(children).includes('\n');
    return isBlock ? (
      <code
        className="block bg-muted text-foreground rounded-md p-4 text-[13px] sm:text-sm font-mono leading-relaxed overflow-x-auto"
        {...p}
      >
        {children}
      </code>
    ) : (
      <code
        className="bg-muted text-foreground rounded px-1.5 py-0.5 text-[0.95em] font-mono"
        {...p}
      >
        {children}
      </code>
    );
  },
  pre: (p) => <pre className="my-5" {...p} />,
  table: (p) => (
    <div className="my-5 overflow-x-auto rounded-md border border-rule">
      <table className="min-w-full border-collapse text-sm" {...p} />
    </div>
  ),
  th: (p) => (
    <th
      className="border-b border-rule bg-muted/60 px-3 py-2 text-left font-display font-medium text-sm"
      {...p}
    />
  ),
  td: (p) => <td className="border-b border-rule/60 px-3 py-2 align-top" {...p} />,
  a: ({ href, ...p }) => {
    const isExternal = !!href && /^https?:/.test(href);
    return (
      <a
        className="text-primary underline underline-offset-4 decoration-primary/50 hover:decoration-primary"
        href={href}
        target={isExternal ? '_blank' : undefined}
        rel={isExternal ? 'noopener noreferrer' : undefined}
        {...p}
      />
    );
  },
  hr: () => <hr className="my-10 border-0 border-t border-dashed border-rule" />,
  strong: (p) => <strong className="font-semibold text-foreground" {...p} />,
  em: (p) => <em className="italic" {...p} />,
};

export default function MarkdownView({ body, className }: { body: string; className?: string }) {
  return (
    <div
      className={cn(
        // Full-width on mobile (the dominant target); bound on desktop
        // so prose lines stay legible.
        'max-w-none md:max-w-3xl',
        'text-foreground leading-relaxed text-[16px]',
        className,
      )}
    >
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {body}
      </ReactMarkdown>
    </div>
  );
}
